import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from "react";
import { listMedia, deleteMedia } from "../api/media";
import { uploadMultiple } from "../api/uploads";
function formatSize(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function formatDate(iso) {
    return new Date(iso).toLocaleDateString("tr-TR", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
}
export default function MediaPage() {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [uploading, setUploading] = useState(false);
    const [search, setSearch] = useState("");
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [copiedKey, setCopiedKey] = useState(null);
    const [preview, setPreview] = useState(null);
    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await listMedia();
            setFiles(res.files);
        }
        catch {
            setError("Dosyalar yüklenemedi.");
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);
    const filtered = files.filter((f) => f.filename.toLowerCase().includes(search.toLowerCase()));
    const handleUpload = async (e) => {
        const fileList = e.target.files;
        if (!fileList || fileList.length === 0)
            return;
        setUploading(true);
        setError("");
        try {
            await uploadMultiple(Array.from(fileList));
            await load();
        }
        catch (err) {
            setError(err?.response?.data?.error ||
                "Yükleme başarısız");
        }
        finally {
            setUploading(false);
            e.target.value = "";
        }
    };
    const handleDelete = async (key) => {
        try {
            await deleteMedia(key);
            setFiles((prev) => prev.filter((f) => f.key !== key));
            if (preview?.key === key)
                setPreview(null);
        }
        catch {
            setError("Silme başarısız.");
        }
        finally {
            setDeleteConfirm(null);
        }
    };
    const copyUrl = (file) => {
        navigator.clipboard.writeText(file.url).then(() => {
            setCopiedKey(file.key);
            setTimeout(() => setCopiedKey(null), 2000);
        });
    };
    const totalSize = files.reduce((s, f) => s + f.size, 0);
    return (_jsxs("div", { children: [_jsxs("div", { className: "page-header", style: { display: "flex", alignItems: "center", gap: 12 }, children: [_jsxs("div", { style: { flex: 1 }, children: [_jsx("h2", { className: "page-title", children: "Medya K\u00FCt\u00FCphanesi" }), !loading && (_jsxs("p", { style: { margin: 0, fontSize: 13, color: "var(--text-muted)" }, children: [files.length, " dosya \u00B7 Toplam ", formatSize(totalSize)] }))] }), _jsxs("label", { style: {
                            display: "inline-flex", alignItems: "center", gap: 6,
                            padding: "8px 18px",
                            background: "var(--primary, #4f46e5)", color: "#fff",
                            borderRadius: 6, cursor: uploading ? "not-allowed" : "pointer",
                            fontSize: 14, fontWeight: 500,
                            opacity: uploading ? 0.7 : 1,
                        }, children: [uploading ? "Yükleniyor…" : "+ Görsel Yükle", _jsx("input", { type: "file", accept: "image/*", multiple: true, onChange: handleUpload, style: { display: "none" }, disabled: uploading })] }), _jsx("button", { onClick: load, style: {
                            padding: "8px 14px", borderRadius: 6,
                            border: "1px solid var(--border)", background: "none",
                            cursor: "pointer", fontSize: 13,
                        }, title: "Yenile", children: "\u21BB" })] }), error && (_jsxs("div", { className: "alert alert-error", style: { marginBottom: 16 }, children: [error, _jsx("button", { onClick: () => setError(""), style: { marginLeft: 8, background: "none", border: "none", cursor: "pointer", fontWeight: 700 }, children: "\u00D7" })] })), _jsx("div", { className: "card", style: { marginBottom: 16, padding: "12px 16px" }, children: _jsx("input", { type: "text", placeholder: "Dosya ad\u0131nda ara\u2026", value: search, onChange: (e) => setSearch(e.target.value), style: {
                        width: "100%", padding: "8px 12px",
                        border: "1px solid var(--border)", borderRadius: 6,
                        fontSize: 13, outline: "none",
                    } }) }), loading ? (_jsx("div", { style: { textAlign: "center", padding: 80, color: "var(--text-muted)" }, children: "Y\u00FCkleniyor\u2026" })) : filtered.length === 0 ? (_jsx("p", { className: "empty-text", children: search ? "Arama sonucu bulunamadı." : "Henüz görsel yüklenmemiş." })) : (_jsx("div", { className: "image-grid", style: {
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                    gap: 12,
                }, children: filtered.map((file) => (_jsxs("div", { className: "image-thumb", style: { position: "relative", cursor: "pointer", borderRadius: 8, overflow: "hidden" }, onClick: () => setPreview(file), children: [_jsx("img", { src: file.url, alt: file.filename, loading: "lazy", style: { width: "100%", height: 120, objectFit: "cover", display: "block" } }), _jsxs("div", { className: "image-info", style: { padding: "6px 8px" }, children: [_jsx("span", { style: {
                                        fontSize: 11, display: "block",
                                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                    }, children: file.filename }), _jsx("span", { style: { fontSize: 10, color: "var(--text-muted)" }, children: formatSize(file.size) })] }), _jsxs("div", { className: "image-actions", style: { padding: "4px 6px", gap: 4 }, children: [_jsx("button", { onClick: (e) => { e.stopPropagation(); copyUrl(file); }, className: "btn-sm btn-secondary", style: { fontSize: 10 }, children: copiedKey === file.key ? "✓" : "URL" }), _jsx("button", { onClick: (e) => { e.stopPropagation(); setDeleteConfirm(file.key); }, className: "btn-sm btn-danger", style: { fontSize: 10 }, children: "Sil" })] })] }, file.key))) })), deleteConfirm && (_jsx("div", { style: {
                    position: "fixed", inset: 0, zIndex: 1000,
                    background: "rgba(0,0,0,0.5)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }, children: _jsxs("div", { style: {
                        background: "var(--bg-surface, #fff)", borderRadius: 10,
                        padding: 24, width: 340, boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                    }, children: [_jsx("h4", { style: { margin: "0 0 8px", fontSize: 16 }, children: "G\u00F6rseli sil?" }), _jsx("p", { style: { margin: "0 0 16px", fontSize: 13, color: "var(--text-muted)", wordBreak: "break-all" }, children: deleteConfirm }), _jsxs("div", { style: { display: "flex", gap: 8, justifyContent: "flex-end" }, children: [_jsx("button", { onClick: () => setDeleteConfirm(null), style: { padding: "6px 14px", borderRadius: 6, border: "1px solid var(--border)", background: "none", cursor: "pointer", fontSize: 13 }, children: "\u0130ptal" }), _jsx("button", { onClick: () => handleDelete(deleteConfirm), style: { padding: "6px 14px", borderRadius: 6, border: "none", background: "#dc2626", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 500 }, children: "Sil" })] })] }) })), preview && (_jsx("div", { style: {
                    position: "fixed", inset: 0, zIndex: 1000,
                    background: "rgba(0,0,0,0.8)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }, onClick: () => setPreview(null), children: _jsxs("div", { style: {
                        background: "var(--bg-surface, #fff)", borderRadius: 12,
                        overflow: "hidden", maxWidth: "min(800px, 94vw)",
                        boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
                    }, onClick: (e) => e.stopPropagation(), children: [_jsx("img", { src: preview.url, alt: preview.filename, style: { width: "100%", maxHeight: "60vh", objectFit: "contain", display: "block", background: "#000" } }), _jsxs("div", { style: { padding: "14px 18px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }, children: [_jsxs("div", { style: { flex: 1 }, children: [_jsx("div", { style: { fontSize: 14, fontWeight: 500, wordBreak: "break-all" }, children: preview.filename }), _jsxs("div", { style: { fontSize: 12, color: "var(--text-muted)", marginTop: 2 }, children: [formatSize(preview.size), " \u00B7 ", formatDate(preview.lastModified)] })] }), _jsx("button", { onClick: () => copyUrl(preview), className: "btn btn-secondary", style: { fontSize: 12 }, children: copiedKey === preview.key ? "✓ Kopyalandı" : "URL Kopyala" }), _jsx("button", { onClick: () => { setDeleteConfirm(preview.key); setPreview(null); }, className: "btn btn-danger", style: { fontSize: 12 }, children: "Sil" }), _jsx("button", { onClick: () => setPreview(null), style: { padding: "6px 14px", borderRadius: 6, border: "1px solid var(--border)", background: "none", cursor: "pointer", fontSize: 12 }, children: "Kapat" })] })] }) }))] }));
}
